/**
 * Create / edit a KPI: one large modal, as in AgencyAnalytics. Title and
 * client on top; the metric drill-down, then the live preview, on the left;
 * Metric Filters, the target and the alert on the right. Saving keeps the
 * modal open with Save disabled until something changes again.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { reportsApi, type Catalogue, type Client, type Filter } from '../reports/reports-api.ts';
import { toTyped } from './kpi-format.ts';
import { alertFormOf, alertOf, targetFormOf, targetOf } from './kpi-form.ts';
import { KpiFilterPanel } from './KpiFilterPanel.tsx';
import { AlertPanel, TargetPanel, type AlertForm, type TargetForm } from './KpiGoalPanels.tsx';
import { KpiMetricPicker, type MetricChoice } from './KpiMetricPicker.tsx';
import { KpiPreview } from './KpiPreview.tsx';
import { kpisApi, type Kpi, type KpiDraft, type KpiVisual } from './kpis-api.ts';
import { useKpiCatalogue } from './use-kpi-catalogue.ts';

const DEFAULT_TIMEZONE = 'Africa/Johannesburg';

export function KpiModal(props: {
  organisationId: string;
  catalogue: Catalogue;
  clients: Client[];
  clientId: string | null;
  kpi: Kpi | null;
  onClose: () => void;
  onSaved: (kpi: Kpi) => void;
}): ReactNode {
  const { kpi, organisationId } = props;
  const [clientId, setClientId] = useState(
    kpi?.business_unit_id ?? props.clientId ?? props.clients[0]?.id ?? '',
  );
  const [title, setTitle] = useState(kpi?.title ?? '');
  const [choice, setChoice] = useState<MetricChoice | null>(
    kpi ? { source: kpi.source, metric: kpi.metric, visual: kpi.visual } : null,
  );
  const [filters, setFilters] = useState<Filter[]>(kpi?.filters ?? []);
  const [secondary, setSecondary] = useState<string | null>(kpi?.secondary_dimension ?? null);
  const catalogue = useKpiCatalogue(organisationId, clientId, props.catalogue);
  const source = catalogue.sources.find((item) => item.key === choice?.source);
  const metric = source?.metrics.find((item) => item.key === choice?.metric);
  const format = metric?.format ?? 'number';
  const [target, setTarget] = useState<TargetForm>(() => targetFormOf(kpi, format));
  const [alert, setAlert] = useState<AlertForm>(() => alertFormOf(kpi, format));
  const [attached, setAttached] = useState<{ source: string; label: string | null }[]>([]);
  const [dirty, setDirty] = useState(kpi === null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const client = props.clients.find((item) => item.id === clientId);
  const timezone = client?.timezone ?? DEFAULT_TIMEZONE;

  useEffect(() => {
    if (!clientId) return;
    reportsApi
      .sources(organisationId, clientId)
      .then(setAttached)
      .catch(() => setAttached([]));
  }, [organisationId, clientId]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && props.onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.onClose]);

  const touched =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setDirty(true);
    };
  const connected = new Set([
    ...attached.map((item) => item.source),
    ...catalogue.sources.filter((item) => !item.needs_attachment).map((item) => item.key),
  ]);
  const draft: KpiDraft | null = choice
    ? {
        business_unit_id: clientId,
        title: title.trim() || metric?.label || choice.metric,
        source: choice.source,
        metric: choice.metric,
        filters,
        secondary_dimension: secondary,
        visual: choice.visual,
        target: null,
        alert: null,
      }
    : null;

  const save = async () => {
    if (!draft) return;
    const builtTarget = targetOf(target, format);
    const builtAlert = alertOf(alert, format);
    const missing = [builtTarget, builtAlert].find((item) => typeof item === 'string');
    if (typeof missing === 'string') return setProblem(missing);
    setSaving(true);
    setProblem(null);
    try {
      const body = {
        ...draft,
        target: builtTarget as KpiDraft['target'],
        alert: builtAlert as KpiDraft['alert'],
      };
      const saved = kpi
        ? await kpisApi.update(organisationId, kpi.id, body)
        : await kpisApi.create(organisationId, body);
      setDirty(false);
      props.onSaved(saved);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The KPI could not be saved.');
    } finally {
      setSaving(false);
    }
  };
  const suggest = async (): Promise<string | null> => {
    if (!draft) return null;
    const suggestion = await kpisApi.suggest(organisationId, draft);
    if (suggestion.value === null) return 'No data last month to suggest from.';
    touched(setTarget)({
      ...target,
      value: String(toTyped(suggestion.value, format, target.condition)),
    });
    return `Last month (${suggestion.range.from} to ${suggestion.range.to}).`;
  };

  return (
    <div className="rc-scrim">
      <div className="kp-modal" role="dialog" aria-modal="true" aria-labelledby="kp-modal-title">
        <header className="kp-modal-head">
          <h2 id="kp-modal-title" className="sr-only">
            {kpi ? `Edit ${kpi.title}` : 'Create KPI'}
          </h2>
          <label className="kp-title-field">
            <span className="sr-only">KPI title</span>
            <input
              value={title}
              placeholder={metric?.label ?? 'KPI title, e.g. Cost Analysis'}
              maxLength={120}
              onChange={(event) => touched(setTitle)(event.target.value)}
              autoFocus
            />
          </label>
          {props.clientId || kpi ? (
            <span className="kp-chip">{client?.name ?? kpi?.client_name}</span>
          ) : (
            <label className="kp-inline">
              Client
              <select
                value={clientId}
                onChange={(event) => touched(setClientId)(event.target.value)}
              >
                {props.clients.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="kp-close" aria-label="Close" onClick={props.onClose}>
            ×
          </button>
        </header>
        <div className="kp-modal-body">
          <div className="kp-main">
            {!choice || !draft || !source ? (
              <KpiMetricPicker
                catalogue={catalogue}
                connected={connected}
                onPick={touched(setChoice)}
              />
            ) : (
              <>
                <button type="button" className="ex-link" onClick={() => touched(setChoice)(null)}>
                  ‹ Change metric
                </button>
                <KpiPreview
                  organisationId={organisationId}
                  clientId={clientId}
                  timezone={timezone}
                  catalogue={catalogue}
                  draft={draft}
                  kpi={dirty ? null : kpi}
                  onVisual={(visual: KpiVisual) => touched(setChoice)({ ...choice, visual })}
                />
              </>
            )}
          </div>
          {choice && source && (
            <aside className="kp-rail" aria-label="KPI settings">
              <KpiFilterPanel
                organisationId={organisationId}
                clientId={clientId}
                timezone={timezone}
                source={source}
                metric={choice.metric}
                properties={attached
                  .filter((item) => item.source === source.key)
                  .map((item) => item.label ?? 'Account')}
                filters={filters}
                secondaryDimension={secondary}
                onFilters={touched(setFilters)}
                onSecondaryDimension={touched(setSecondary)}
              />
              <TargetPanel
                form={target}
                format={format}
                onChange={touched(setTarget)}
                onSuggest={suggest}
              />
              <AlertPanel form={alert} format={format} onChange={touched(setAlert)} />
            </aside>
          )}
        </div>
        <footer className="kp-modal-foot">
          {problem && (
            <p className="kp-problem" role="alert">
              {problem}
            </p>
          )}
          <span className="ag-spacer" />
          <button type="button" className="btn secondary" onClick={props.onClose}>
            {dirty ? 'Cancel' : 'Close'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={!draft || !dirty || saving}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}
